import React from 'react';
import styled from 'astroturf/react';

import { Button } from 'js/design_system/Button';

const Card = styled.div<{ layout?: 'half' }>`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  cursor: pointer;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-medium, #535366);
  border-radius: var(--border-radius-radius-md, 8px);
  text-align: left;

  &.layout-half {
    flex: 0 1 calc(50% - 24px);
  }

  @include breakpoint(small, medium) {
    flex: 0 1 auto;
    width: 100%;

    &.layout-half {
      flex: 0 1 auto;
    }
  }
`;

const HeaderRow = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs-12, 12px);
  padding: var(--spacing-xs-12, 12px) var(--spacing-xs-16, 16px)
    var(--spacing-xs-12, 12px) 20px;
  border-bottom: 1px solid var(--stroke-medium, #535366);

  @include breakpoint(small, medium) {
    padding: var(--spacing-xs-12, 12px) var(--spacing-xs-12, 12px)
      var(--spacing-xs-12, 12px) var(--spacing-xs-16, 16px);
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
  min-width: 0;
`;

const CardTitle = styled.p`
  @import 'stylesheets/design_system/breakpoint';

  margin: 0;
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-lg-bold-size, 20px);
  line-height: var(--type-body-lg-bold-line-height, 32px);
  font-weight: var(--type-body-lg-bold-font-weight, 600);
  color: var(--text-primary, #141413);

  @include breakpoint(small, medium) {
    font-size: var(--type-body-md-bold-size, 16px);
    line-height: var(--type-body-md-bold-line-height, 20px);
  }
`;

const Sublabel = styled.p`
  margin: 0;
  font-family:
    var(--type-font-family-mono, 'IBM Plex Mono'), 'Courier New', monospace;
  font-size: var(--type-body-sm-size, 14px);
  line-height: 1;
  font-weight: 500;
  letter-spacing: 0.14px;
  text-transform: uppercase;
  color: var(--text-secondary, #535366);
  white-space: nowrap;
`;

const CtaDesktop = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  flex-shrink: 0;

  @include breakpoint(small, medium) {
    display: none;
  }
`;

const CtaMobile = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: none;

  @include breakpoint(small, medium) {
    display: block;
    flex-shrink: 0;
  }
`;

const HighlightPanel = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  padding: 20px var(--spacing-sm-24, 24px) 20px 20px;
  background: var(--background-white, #ffffff);

  @include breakpoint(small, medium) {
    padding: 20px var(--spacing-xs-12, 12px) 20px var(--spacing-xs-16, 16px);
  }
`;

const RowsPanel = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  position: relative;
  height: 360px;
  overflow: hidden;
  margin: 0 var(--spacing-xs-16, 16px) var(--spacing-xs-16, 16px);
  padding: 20px 20px 0;
  background: var(--background-light-blue, #eef2f9);
  border-radius: var(--border-radius-radius-md, 8px);

  @include breakpoint(small, medium) {
    padding: 20px var(--spacing-xs-12, 12px) 0 var(--spacing-xs-16, 16px);
  }
`;

export type DownloadOptionCardProps = {
  title: string;
  sublabel: string;
  ctaLabel: string;
  ctaLabelMobile?: string;
  onCta: () => void;
  onSurfaceClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  layout?: 'half';
  currentPaper: React.ReactNode;
  rows?: React.ReactNode;
};

export const DownloadOptionCard: React.FC<DownloadOptionCardProps> = ({
  title,
  sublabel,
  ctaLabel,
  ctaLabelMobile,
  onCta,
  onSurfaceClick,
  layout,
  currentPaper,
  rows,
}) => (
  <Card layout={layout} onClick={onSurfaceClick}>
    <HeaderRow>
      <TitleBlock>
        <CardTitle>{title}</CardTitle>
        <Sublabel>{sublabel}</Sublabel>
      </TitleBlock>
      <CtaDesktop>
        <Button size="sm" icon="arrow_downward" onClick={onCta}>
          {ctaLabel}
        </Button>
      </CtaDesktop>
      <CtaMobile>
        <Button size="sm" onClick={onCta}>
          {ctaLabelMobile ?? ctaLabel}
        </Button>
      </CtaMobile>
    </HeaderRow>
    <HighlightPanel>{currentPaper}</HighlightPanel>
    {rows && <RowsPanel>{rows}</RowsPanel>}
  </Card>
);

export default DownloadOptionCard;
